"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

function YandexLogo({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z" fill="#FC3F1D"/>
      <path d="M13.6 18.4H15.2V5.6H12.76C10.0893 5.6 8.72 7.04 8.72 9.04C8.72 10.76 9.56 11.72 11.08 12.76L8.48 18.4H10.24L13.04 12.32L11.88 11.56C10.6 10.68 9.96 9.96 9.96 8.88C9.96 7.68 10.8 6.84 12.36 6.84H13.6V18.4Z" fill="white"/>
    </svg>
  );
}

function VKLogo({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z" fill="#0077FF"/>
      <path d="M12.7608 17.2C7.39277 17.2 4.33477 13.56 4.20077 7.40002H6.60077C6.69477 11.88 8.69477 13.8 10.2808 14.2V7.40002H12.5208V11.16C14.0808 10.992 15.7208 9.20002 16.2808 7.40002H18.5208C18.0888 9.60002 16.3208 11.4 15.0408 12.16C16.3208 12.78 18.3208 14.34 19.0408 17.2H16.5608C16.0008 15.44 14.5208 13.96 12.5208 13.76V17.2H12.7608Z" fill="white"/>
    </svg>
  );
}

function TelegramLogo({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z" fill="#26A5E4"/>
      <path d="M5.432 11.873l8.772-3.39c.6-.217 2.631-.895 2.631-.895s.938-.365.86.52c-.026.365-.234 1.644-.442 3.026l-.651 4.098s-.052.599-.494.703-.924-.296-.924-.296l-2.553-1.748-1.356.938a.57.57 0 01-.468.078l.234-2.19 5.794-5.196s.26-.286 0-.286-.338.078-.599.286l-7.193 4.565-2.475-.851s-.364-.13-.312-.442c.052-.312.442-.494.442-.494z" fill="white"/>
    </svg>
  );
}

const TELEGRAM_BOT_ID = "8490943935";

type TelegramLogin = {
  auth: (
    options: { bot_id: string; request_access?: boolean },
    callback: (data: Record<string, string> | false) => void,
  ) => void;
};

function getTelegramLogin(): TelegramLogin | undefined {
  return (window as unknown as { Telegram?: { Login?: TelegramLogin } }).Telegram?.Login;
}

function useTelegramLogin(onSuccess: (user: Record<string, string>) => void, onCancel: () => void) {
  const scriptLoaded = useRef(false);
  const onSuccessRef = useRef(onSuccess);
  const onCancelRef = useRef(onCancel);
  onSuccessRef.current = onSuccess;
  onCancelRef.current = onCancel;

  useEffect(() => {
    if (scriptLoaded.current || document.getElementById("telegram-login-sdk")) return;
    scriptLoaded.current = true;
    const script = document.createElement("script");
    script.id = "telegram-login-sdk";
    script.src = "https://telegram.org/js/telegram-widget.js?22";
    script.async = true;
    document.head.appendChild(script);
  }, []);

  const login = useCallback(() => {
    const tgLogin = getTelegramLogin();
    if (!tgLogin) {
      // Fallback: open Telegram OAuth popup manually. The popup can't call us back
      // (no postMessage bridge on oauth.telegram.org), so we poll `popup.closed` and
      // run the cancel path when the user closes it without completing. 120s hard
      // timeout covers the case where popups are blocked or the reference is lost.
      const origin = encodeURIComponent(window.location.origin);
      const popup = window.open(
        `https://oauth.telegram.org/auth?bot_id=${TELEGRAM_BOT_ID}&origin=${origin}&request_access=write`,
        "telegram_auth",
        `width=550,height=470,left=${(screen.width - 550) / 2},top=${(screen.height - 470) / 2}`
      );
      if (!popup) {
        onCancelRef.current();
        return;
      }
      const stopAt = Date.now() + 120_000;
      const poll = window.setInterval(() => {
        if (popup.closed) {
          window.clearInterval(poll);
          onCancelRef.current();
          return;
        }
        if (Date.now() > stopAt) {
          window.clearInterval(poll);
          try { popup.close(); } catch { /* cross-origin */ }
          onCancelRef.current();
        }
      }, 500);
      return;
    }
    tgLogin.auth(
      { bot_id: TELEGRAM_BOT_ID, request_access: true },
      (data) => {
        if (data) onSuccessRef.current(data);
        else onCancelRef.current();
      }
    );
  }, []);

  return login;
}

type Provider = "yandex" | "vk" | "telegram";

interface OAuthButtonsProps {
  isLoading?: boolean;
  dividerBg?: string;
}

export function OAuthButtons({ isLoading: externalLoading, dividerBg = "bg-card" }: OAuthButtonsProps) {
  const [activeProvider, setActiveProvider] = useState<Provider | null>(null);

  const telegramLogin = useTelegramLogin(
    (user) => {
      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(user)) {
        if (value) params.set(key, String(value));
      }
      window.location.href = `/api/v1/auth/oauth/telegram/callback?${params.toString()}`;
    },
    () => {
      setActiveProvider(null);
      toast.info("Вход через Telegram отменён");
    }
  );

  const handleRedirect = (provider: Exclude<Provider, "telegram">) => {
    setActiveProvider(provider);
    window.location.href = `/api/v1/auth/oauth/${provider}`;
  };

  const handleTelegram = () => {
    setActiveProvider("telegram");
    try {
      telegramLogin();
    } catch (err) {
      console.error("Telegram login failed", err);
      setActiveProvider(null);
      toast.error("Не удалось открыть вход через Telegram");
    }
  };

  const anyLoading = !!externalLoading || activeProvider !== null;

  const renderButton = (
    provider: Provider,
    logo: React.ReactNode,
    label: string,
    onClick: () => void,
  ) => (
    <Button
      variant="outline"
      className="w-full border-border hover:bg-muted/50 text-foreground"
      onClick={onClick}
      disabled={anyLoading}
    >
      {activeProvider === provider ? (
        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
      ) : (
        logo
      )}
      {label}
    </Button>
  );

  return (
    <>
      <div className="relative my-4">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t border-border" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className={`${dividerBg} px-2 text-muted-foreground`}>или</span>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        {renderButton("yandex", <YandexLogo className="mr-2 h-5 w-5" />, "Войти через Яндекс", () => handleRedirect("yandex"))}
        {renderButton("vk", <VKLogo className="mr-2 h-5 w-5" />, "Войти через VK", () => handleRedirect("vk"))}
        {renderButton("telegram", <TelegramLogo className="mr-2 h-5 w-5" />, "Войти через Telegram", handleTelegram)}
      </div>
    </>
  );
}
